import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Users, CheckCircle2, XCircle, Calendar, FolderKanban, ListChecks, Clock, AlertTriangle } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import PageHeader from '@/components/PageHeader';
import { Card, StatCard } from '@/components/Card';
import Loader from '@/components/Loader';
import { reportService, attendanceService } from '@/services';
import { fmtTime, isLunchOver } from '@/utils/format';

const COLORS = ['#3a64ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function AdminDashboard() {
  const overview = useQuery({ queryKey: ['report', 'overview'], queryFn: reportService.overview });
  const todaySnap = useQuery({ queryKey: ['attendance', 'admin-today'], queryFn: attendanceService.adminToday });
  const totals = overview.data?.totals;
  const trend = (overview.data?.attendanceTrend || []).map((t) => ({
    date: dayjs(t._id).format('DD MMM'),
    present: t.present,
    absent: t.absent,
    late: t.late,
  }));

  const taskBreakdown = totals
    ? [
        { name: 'Completed', value: totals.completedTasks },
        { name: 'Pending', value: totals.pendingTasks },
        { name: 'Other', value: Math.max(0, totals.totalTasks - totals.completedTasks - totals.pendingTasks) },
      ].filter((d) => d.value > 0)
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Overview"
        subtitle={dayjs().format('dddd, DD MMMM YYYY')}
      />

      {overview.isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} color="brand" label="Total employees" value={totals?.totalEmployees ?? 0} />
            <StatCard icon={CheckCircle2} color="emerald" label="Present today" value={totals?.presentToday ?? 0} hint={`${totals?.onLeaveToday ?? 0} on leave`} />
            <StatCard icon={XCircle} color="rose" label="Absent today" value={totals?.absentToday ?? 0} />
            <StatCard icon={Calendar} color="amber" label="Pending leaves" value={totals?.pendingLeaves ?? 0} />
            <StatCard icon={FolderKanban} color="violet" label="Active projects" value={totals?.activeProjects ?? 0} />
            <StatCard icon={ListChecks} color="sky" label="Total tasks" value={totals?.totalTasks ?? 0} />
            <StatCard icon={CheckCircle2} color="emerald" label="Completed tasks" value={totals?.completedTasks ?? 0} />
            <StatCard icon={AlertTriangle} color="amber" label="Pending tasks" value={totals?.pendingTasks ?? 0} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <div className="font-semibold mb-3">Attendance trend (last 30 days)</div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3a64ff" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#3a64ff" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="present" stroke="#3a64ff" fill="url(#g1)" />
                    <Area type="monotone" dataKey="late" stroke="#f59e0b" fill="url(#g2)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card>
              <div className="font-semibold mb-3">Task breakdown</div>
              {taskBreakdown.length === 0 ? (
                <div className="h-56 grid place-items-center text-sm text-slate-500">No tasks yet</div>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={taskBreakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} label>
                        {taskBreakdown.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2">
              <Clock size={18} /> Today's check-ins
            </div>
            {todaySnap.isLoading ? (
              <Loader />
            ) : (todaySnap.data?.items || []).length === 0 ? (
              <div className="text-sm text-slate-500 py-4">No check-ins yet today.</div>
            ) : (
              <div className="table-wrap">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="th">Employee</th>
                      <th className="th">Department</th>
                      <th className="th">Check In</th>
                      <th className="th">Check Out</th>
                      <th className="th">Lunch</th>
                      <th className="th">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todaySnap.data.items.map((r) => (
                      <tr key={r._id}>
                        <td className="td font-medium">{r.user?.name}</td>
                        <td className="td">{r.user?.department}</td>
                        <td className="td">{fmtTime(r.checkInAt)}</td>
                        <td className="td">{fmtTime(r.checkOutAt)}</td>
                        <td className="td whitespace-nowrap">
                          {r.lunchStartAt ? (
                            <span className={isLunchOver(r.lunchMinutes) ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                              {fmtTime(r.lunchStartAt)}{r.lunchEndAt ? ` – ${fmtTime(r.lunchEndAt)}` : ' · on lunch'}
                              {r.lunchMinutes ? <span className="text-xs"> · {r.lunchMinutes}m</span> : ''}
                              {isLunchOver(r.lunchMinutes) && (
                                <span className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 text-[10px] font-semibold">Over 1h</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="td capitalize">{r.status}{r.isLate ? ' · late' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
